import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context';
import { getHistory, type HistoryItem } from '../../api/event';
import { ActivityIndicator } from '../../components/ui';
import { ListItemHistory } from '../../components/lists';
import styles from './HistoryPage.module.css';

interface LocationState {
  cid?: string;
}

export function HistoryPage() {
  const location = useLocation();
  const { loginKey } = useAuth();
  const { cid } = (location.state as LocationState) || {};

  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [emptyText, setEmptyText] = useState('');
  const [firstId, setFirstId] = useState<number | null>(null);
  const [lastId, setLastId] = useState<number | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);

  const fetchHistory = useCallback(
    async (beforeId?: number, afterId?: number) => {
      if (!cid || !loginKey) {
        setEmptyText('Error: Missing Account Info.');
        return;
      }

      setLoading(true);
      setEmptyText('');

      const response = await getHistory({ loginKey, cid, beforeId, afterId });
      setLoading(false);

      if (!response.ok) {
        setEmptyText(`Error fetching history: ${response.problem}`);
        setHasOlder(false);
        setHasNewer(false);
        return;
      }

      if (response.data?.error || !response.data?.history) {
        setEmptyText(response.data?.error?.message || 'Error fetching history');
        setHasOlder(false);
        setHasNewer(false);
        return;
      }

      if (!response.data.history.length) {
        if (!beforeId && !afterId) {
          setEmptyText('No history found.');
        } else if (beforeId) {
          setEmptyText('End of history.');
        }
        return;
      }

      const filtered = response.data.history.filter((item) => {
        const delta = item.change?.p1_delta?.delta;
        return delta !== undefined && delta !== null && delta !== 0;
      });
      const sortedHistory = [...filtered].sort((a, b) => b.id - a.id);
      setHistory(sortedHistory);
      setFirstId(response.data.first_id || null);
      setLastId(response.data.last_id || null);

      if (!beforeId && !afterId) {
        setHasOlder(response.data.history.length >= 25);
        setHasNewer(false);
      } else if (beforeId) {
        setHasNewer(true);
        setHasOlder(response.data.history.length >= 25);
      } else if (afterId) {
        setHasNewer(response.data.history.length >= 25);
        setHasOlder(true);
      }
    },
    [cid, loginKey]
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const loadOlder = () => {
    if (firstId) {
      fetchHistory(firstId);
    }
  };

  const loadNewer = () => {
    if (lastId) {
      fetchHistory(undefined, lastId);
    }
  };

  return (
    <div className={styles.container}>
      <ActivityIndicator visible={loading} />

      {hasNewer && (
        <button className={styles.navButton} onClick={loadNewer}>
          ↑ Load Newer
        </button>
      )}

      <div className={styles.list}>
        {history.length === 0 ? (
          <p className={styles.emptyText}>{emptyText}</p>
        ) : (
          history.map((item) => (
            <div key={item.id} className={styles.listItem}>
              <ListItemHistory item={item} />
            </div>
          ))
        )}
      </div>

      {hasOlder && (
        <button className={styles.navButton} onClick={loadOlder}>
          ↓ Load Older
        </button>
      )}
    </div>
  );
}

export default HistoryPage;
